import { createFormatAdapter, createPlugin, type ValidationIssue } from '@customerio/dryad';
import {
  parseAttributeEnvelope,
  unwrapSupportableAttributeEnvelope,
  type WireAttribute,
} from '../../attribute-envelope';
import {
  isTwoValueFilterOperator,
  isUnaryFilterOperator,
  validateAttributeFilterState,
  type AttributeFilterState,
} from '../../attribute-filter-state';
import { EMPTY_ATTRIBUTE_FIELD_POLICY, isSupportedOperator, type GroupableFamily } from '../../operator-groups';
import { isRecord } from '@/utils/objects';
import { safeQueryDecode, type QueryValue } from '@/utils/condition-query-encoding';
import type { JsonObject, JsonValue } from '@/utils/json';

export const OBJECT_FILTER_FORMAT = 'objects-filter';
export const OBJECT_ATTRIBUTE_TYPE = 'object_attribute';
export const OBJECT_FILTER_MAX_DEPTH = 1;

export const OBJECT_FILTER_OPERATOR_FAMILIES = ['equality', 'existence'] as const satisfies readonly GroupableFamily[];

const WIRE_KEYS = new Set(['field', 'operator', 'value', 'value2', 'type_id']);

export type ObjectAttributeFilterState = AttributeFilterState & { typeId: number | string };
export type ObjectFilterWire = JsonObject | JsonObject[];
export type ObjectFilterSummary = {
  operator: string | null;
  objectAttributes: number;
  relationshipAttributes: number;
};
type ObjectAttributePayload = {
  field?: string;
  value?: string | number;
  value2?: string | number;
  operator?: string;
  type_id: number | string;
};
type ObjectAttributeFilter = { object_attribute: ObjectAttributePayload };

function isOptionalString(value: unknown): boolean {
  return value == null || typeof value === 'string';
}

function parseObjectTypeId(value: unknown): number | string | null {
  return typeof value === 'number' || typeof value === 'string' ? value : null;
}

function isObjectAttributePayload(attribute: WireAttribute): boolean {
  return (
    Object.keys(attribute).every((key) => WIRE_KEYS.has(key)) &&
    isOptionalString(attribute.value) &&
    isOptionalString(attribute.value2)
  );
}

function parseObjectAttributeFilter(json: unknown): ObjectAttributeFilterState | null {
  const unwrapped = unwrapSupportableAttributeEnvelope(json, OBJECT_ATTRIBUTE_TYPE);
  if (unwrapped == null) return null;
  if (!isObjectAttributePayload(unwrapped.attribute)) return null;
  const typeId = parseObjectTypeId(unwrapped.attribute.type_id);
  if (typeId == null) return null;
  const { operator } = unwrapped;
  if (!isSupportedOperator(operator, OBJECT_FILTER_OPERATOR_FAMILIES, false)) return null;
  if (unwrapped.attribute.value2 != null && !isTwoValueFilterOperator(operator)) return null;
  if (isUnaryFilterOperator(operator) && unwrapped.attribute.value != null) return null;
  return {
    ...parseAttributeEnvelope(json, OBJECT_ATTRIBUTE_TYPE),
    typeId,
  };
}

function withoutBlankValues(values: Record<string, string | number | null>) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value != null && value !== ''));
}

type ObjectAttributeFilterInput = {
  field: string;
  operator: string;
  value: string | number | null;
  value2: string | number | null;
  typeId: number | string;
};

function serializeObjectAttributeFilter(
  state: ObjectAttributeFilterInput,
): ObjectAttributeFilter | { not: ObjectAttributeFilter } {
  const negated = state.operator.startsWith('!');
  const operator = negated ? state.operator.slice(1) : state.operator;
  const payload = withoutBlankValues({
    field: state.field,
    operator,
    value: isUnaryFilterOperator(state.operator) ? null : state.value,
    value2: isTwoValueFilterOperator(state.operator) ? state.value2 : null,
  });
  const filter: ObjectAttributeFilter = { object_attribute: { ...payload, type_id: state.typeId } };
  return negated ? { not: filter } : filter;
}

function legacyWireValue(value: unknown): string | number | null {
  return typeof value === 'string' || typeof value === 'number' ? value : null;
}

function normalizeLegacyObjectAttribute(value: WireAttribute, defaultTypeId: number | string) {
  const operator = typeof value.operator === 'string' ? value.operator : '';
  return serializeObjectAttributeFilter({
    field: typeof value.field === 'string' ? value.field : '',
    operator,
    value: isUnaryFilterOperator(operator) ? null : legacyWireValue(value.value),
    value2: isTwoValueFilterOperator(operator) ? legacyWireValue(value.value2) : null,
    typeId: typeof value.type_id === 'number' || typeof value.type_id === 'string' ? value.type_id : defaultTypeId,
  });
}

function normalizeObjectAttributeTypeIds(wire: QueryValue, defaultTypeId: number | string): QueryValue {
  if (Array.isArray(wire)) return wire;

  if (isRecord(wire.object_attribute)) {
    const typeId = wire.object_attribute.type_id;
    if (typeId == null || String(typeId) !== String(defaultTypeId)) {
      return { ...wire, object_attribute: { ...wire.object_attribute, type_id: defaultTypeId } };
    }
  }

  const normalized = { ...wire };
  for (const key of ['and', 'or']) {
    const group = wire[key];
    if (Array.isArray(group)) {
      normalized[key] = group.map((child) =>
        isRecord(child) ? normalizeObjectAttributeTypeIds(child, defaultTypeId) : child,
      );
    }
  }
  if (isRecord(wire.not)) normalized.not = normalizeObjectAttributeTypeIds(wire.not, defaultTypeId);
  return normalized;
}

function validateObjectAttributeFilter(state: ObjectAttributeFilterState): ValidationIssue[] {
  const errors: ValidationIssue[] = [...validateAttributeFilterState(state, EMPTY_ATTRIBUTE_FIELD_POLICY)];
  if (state.typeId === 0 || state.typeId === '') errors.push({ message: 'Select an object type.' });
  return errors;
}

const objectAttributePlugin = createPlugin({
  type: OBJECT_ATTRIBUTE_TYPE,
  label: 'Attribute',
  parse: parseObjectAttributeFilter,
  serialize: serializeObjectAttributeFilter,
  validate: validateObjectAttributeFilter,
  createDefault: (): ObjectAttributeFilterState => ({
    field: '',
    operator: 'eq',
    value: null,
    value2: null,
    typeId: 0,
  }),
});

export const objectFilterAdapter = createFormatAdapter({ plugins: [objectAttributePlugin] });

export function normalizeObjectFilterWire(wire: QueryValue, objectTypeId?: string) {
  const numericTypeId = Number(objectTypeId);
  const defaultTypeId = objectTypeId == null || Number.isNaN(numericTypeId) ? (objectTypeId ?? 0) : numericTypeId;

  const normalized: QueryValue = Array.isArray(wire)
    ? {
        and: wire.map((value) => {
          if (!isRecord(value) || value.type !== OBJECT_ATTRIBUTE_TYPE) return value;
          return normalizeLegacyObjectAttribute(value, defaultTypeId);
        }),
      }
    : wire;

  return objectTypeId == null ? normalized : normalizeObjectAttributeTypeIds(normalized, defaultTypeId);
}

export function parseObjectFilterWire(value: unknown): ObjectFilterWire | null {
  const parsed = toJsonValue(value);
  if (Array.isArray(parsed)) {
    const objects = parsed.flatMap((entry) => {
      if (typeof entry !== 'object' || entry == null || Array.isArray(entry)) return [];
      return [entry];
    });
    return objects.length === parsed.length ? objects : null;
  }
  return typeof parsed === 'object' && parsed != null ? parsed : null;
}

function toJsonValue(value: unknown): JsonValue {
  if (value == null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) return value.map(toJsonValue);
  if (isRecord(value)) {
    const result: JsonObject = {};
    for (const [key, child] of Object.entries(value)) result[key] = toJsonValue(child);
    return result;
  }
  return null;
}

function countFilterConditions(value: unknown, summary: ObjectFilterSummary): void {
  if (Array.isArray(value)) {
    for (const child of value) countFilterConditions(child, summary);
    return;
  }
  if (!isRecord(value)) return;
  if ('object_attribute' in value) summary.objectAttributes += 1;
  if ('relationship_attribute' in value) summary.relationshipAttributes += 1;
  for (const child of Object.values(value)) countFilterConditions(child, summary);
}

export function summarizeObjectFilters(filters: string | null): ObjectFilterSummary {
  const summary: ObjectFilterSummary = { operator: null, objectAttributes: 0, relationshipAttributes: 0 };
  if (filters == null) return summary;
  const decoded = safeQueryDecode(filters);
  summary.operator = Array.isArray(decoded) ? 'and' : (Object.keys(decoded)[0] ?? 'and');
  countFilterConditions(decoded, summary);
  return summary;
}
